import { useRef } from "react";
import { useButton, useComboBox, useFilter, useSearchField } from "react-aria";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { type ComboBoxStateOptions, useComboBoxState, useSearchFieldState } from "react-stately";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import type { LoadingState } from "@react-types/shared";
import clsx from "clsx";

import { ListBox } from "./ListBox";

import { Spinner } from "~/components/Loader";
import { Media } from "~/components/Media";
import { Popover } from "~/components/Popover";
import { Text } from "~/components/Text";

interface SearchAutocompleteProps<T> extends ComboBoxStateOptions<T> {
  addonRight?: React.ReactNode;
  addonLeft?: React.ReactNode;
  description?: React.ReactNode;
  isLoading?: boolean;
  loadingState?: LoadingState;
  maxHeight?: string;
  showMap?: boolean;
}

const defaultAddonLeft = (
  <MagnifyingGlassIcon aria-hidden="true" className="h-sm w-sm text-gray-500" />
);

export function SearchAutocomplete<T extends object>({
  addonLeft = defaultAddonLeft,
  addonRight,
  maxHeight = "max-h-80",
  showMap,
  ...props
}: SearchAutocompleteProps<T>) {
  let { contains } = useFilter({ sensitivity: "base" });
  let state = useComboBoxState({
    ...props,
    defaultFilter: contains,
    allowsEmptyCollection: props.loadingState !== undefined,
  });

  let inputRef = useRef(null);
  let listBoxRef = useRef(null);
  let popoverRef = useRef(null);

  let { descriptionProps, inputProps, labelProps, listBoxProps } = useComboBox(
    {
      ...props,
      inputRef,
      listBoxRef,
      popoverRef,
    },
    state,
  );

  // Get props for the clear button from useSearchField
  let searchProps = {
    label: props.label,
    "aria-label": inputProps["aria-label"],
    "aria-labelledby": inputProps["aria-labelledby"],
    value: state.inputValue,
    onChange: (v: string) => state.setInputValue(v),
  };

  let searchState = useSearchFieldState(searchProps);
  let { clearButtonProps } = useSearchField(searchProps, searchState, inputRef);
  let clearButtonRef = useRef(null);
  let { buttonProps } = useButton(clearButtonProps, clearButtonRef);

  return (
    <div
      className="relative inline-flex w-full flex-col gap-xs"
      data-component="SearchAutocomplete"
    >
      <label className="font-semibold" data-component="InputLabel" {...labelProps}>
        {props.label}
      </label>
      {props.description && (
        <p className="text-sm text-gray-500" {...descriptionProps}>
          {props.description}
        </p>
      )}
      <Media showFor="mobile">
        <div className="flex">{addonRight}</div>
      </Media>
      <div
        className={clsx(
          "flex flex-1 flex-row items-center gap-xs",
          "overflow-hidden rounded bg-white px-xs",
          // Prevent the focus ring from being cut off the screen
          "mr-[2px]",
          state.isFocused ? "ring-2 ring-brand" : "ring-1 ring-gray-300",
        )}
        data-component="InputWrapper"
        data-focused={state.isFocused}
      >
        {addonLeft}
        <div className="flex flex-1 items-center">
          <input {...inputProps} className="flex-1 p-xs outline-none" ref={inputRef} />
          <div>
            {(props.loadingState === "filtering" || props.loadingState === "loading") && (
              <Spinner size={Spinner.size.SMALL} />
            )}
          </div>
        </div>

        {state.inputValue !== "" && (
          <button
            {...buttonProps}
            className="cursor-pointer text-gray-500 hover:text-gray-600"
            ref={clearButtonRef}
          >
            <XMarkIcon aria-hidden="true" className="h-sm w-sm" />
          </button>
        )}
        {state.inputValue === "" && <Media showFor="desktop">{addonRight}</Media>}
      </div>
      {state.isOpen && (
        <Popover isOpen={state.isOpen} onClose={state.close} popoverRef={popoverRef}>
          <ErrorBoundary FallbackComponent={ErrorFallback} onError={console.error}>
            <ListBox
              {...listBoxProps}
              isLoading={props.loadingState === "loadingMore"}
              listBoxRef={listBoxRef}
              maxHeight={maxHeight}
              state={state}
            />
          </ErrorBoundary>
        </Popover>
      )}
    </div>
  );
}

function ErrorFallback({ resetErrorBoundary }: FallbackProps) {
  return (
    <div className="px-sm" role="alert">
      <button className={"underline"} onClick={resetErrorBoundary}>
        <Text i18nKey="errors.recaptcha" />
      </button>
    </div>
  );
}
